// PanelSourceView — a source file-rendering view shaped as a right side-panel
// pane, realised from the landed sketch for dogfood F4 (the right dock's
// conversation-scoped "files in this chat" panel). Scope, per owner: "räcker
// med titel och filen renderad för tillfället" (title + the rendered file is
// enough for now) — deliberately NO annotation tools, NO editing, NO citations
// UI. Just title + rendered file, narrow-panel shaped. User-facing copy is
// English source through Lingui; the host renders the active locale.
//
// BORROWED AS-IS: `SourceBlock` (the text paragraph vocabulary) and the
// shared `SourceReading` per-kind registry (SourceRenderer.tsx). The
// filename line shows only when it differs from the title (as SourceView's
// header does).
//
// DELIBERATELY NOT reused: the SourceView shell's summary/notes/evidence
// sections, AnnotatedBlock's live select→annotate surface, FindBar. A PDF
// DOES get the paginated reader's page control + Pages|Extracted-text toggle
// (2026-10-01): a chat reference names pages, and the panel must open at them
// and mark them so the author can verify the claim (owner).
//
// THIS IS A PROJECTION: `SourceViewModel` is the exact shape a server
// projection would hand the client for this pane. Two axes, kept ORTHOGONAL
// on purpose (owner correction): whether there's a FILE to show at all
// (`content`: the shared `SourceProjection` sum) is independent of whether
// background ANALYSIS is still running on it (`analysis`: pending | ready). A
// source mid-analysis still has its bytes — the file renders exactly as
// normal, the pending analysis is a small non-blocking affordance layered on
// top, never a state that hides the file behind a full-panel spinner. (This
// mirrors the real `SourceEnrichment` sum in types.ts — `analysing`/`settled`
// describing the SUMMARY sweep, never gating whether the reading surface
// itself shows — applied here to the same shape of problem.) Pure render:
// every datum arrives via `model`; there is no view-ephemeral local state to
// track here (no find bar, no composer — just native scroll,
// which needs no state at all).
//
// CONTENT COVERAGE (dogfood F106): the panel's `content` IS the shared
// `SourceProjection` sum, so every readable kind the full centre `SourceView`
// renders renders inline HERE too. Text keeps the panel's own `TextBody` (no
// select→annotate surface); every other kind delegates to `SourceReading`
// without a `sourceId`, so no annotation chrome leaks in. A genuinely
// un-previewable source shows the shared, honest `unavailable` message.

import { useLingui } from "@lingui/react/macro";
import { type ReactNode, useEffect, useRef } from "react";
import { Badge } from "../Badge/Badge.js";
import { EmptyState } from "../EmptyState/EmptyState.js";
import { Heading } from "../Heading/Heading.js";
import { Icon } from "../Icon/Icon.js";
import { Mono } from "../Mono/Mono.js";
import type { PageSpan } from "../Navigation/types.js";
// The shared reader styles its markup with the `bilda-source-view__*` class
// names owned by SourceView.css; this panel reuses the READER, not the
// SourceView shell, so it imports that stylesheet too (read-only).
import "../SourceView/SourceView.css";
import { SourceReading } from "../SourceView/SourceRenderer.js";
import type { SourceBlock, SourceProjection } from "../SourceView/types.js";
import { Spinner } from "../Spinner/Spinner.js";
import "./PanelSourceView.css";

// The panel's own trimmed metadata — title + author-facing kind label +
// optional filename. `kind` follows `SourceMeta.kind`'s convention (types.ts):
// an author-facing FILE kind ("PDF", "Text file", "Markdown"), never a MIME
// type; empty string ⇒ unknown, the view renders a neutral fallback.
type SourcePanelMeta = {
    readonly title: string;
    readonly kind: string;
    readonly filename?: string;
};

// Background analysis status — a closed sum (never a bare boolean whose
// meaning could silently invert), named after the real `SourceEnrichment`
// sum it mirrors. `pending` renders the small "Analyseras…" affordance
// alongside the (already-rendered) file; `ready` renders nothing extra —
// this panel doesn't show analysis OUTPUT (summary), only whether it's
// still running, so there's no second state to distinguish once it lands.
type AnalysisStatus = { readonly type: "pending" } | { readonly type: "ready" };

// The shell↔panel data seam this sketch stands in for. A closed sum over
// whether the source is reachable at all:
//   - "source": the source exists — its file content and its (orthogonal)
//     analysis status both ride together, so a mid-analysis source still
//     shows its file.
//   - "not-found": the source is gone, or was never reachable — the
//     graceful terminal floor. No `meta`/`content` here on purpose: if the
//     title itself can't be trusted, showing a stale one would be worse
//     than a neutral placeholder.
export type SourceViewModel =
    | {
          readonly type: "source";
          readonly meta: SourcePanelMeta;
          // The shared `SourceProjection` sum (types.ts), so the panel renders
          // every readable kind the full centre view does — no narrower
          // parallel type that silently downgrades image/media/unavailable.
          readonly content: SourceProjection;
          readonly analysis: AnalysisStatus;
      }
    | { readonly type: "not-found" };

export type PanelSourceViewProps = {
    readonly model: SourceViewModel;
    // A page span to open at and mark (a chat tool's read locator: "pages
    // 1-12"), so the author can read and verify the referenced pages.
    readonly focusPages?: PageSpan;
    // Re-upload the original file into this source when its stored content
    // is missing (offered only by that state). Absent ⇒ no button.
    readonly onReupload?: () => void;
    readonly className?: string;
};

export const PanelSourceView = ({
    model,
    focusPages,
    onReupload,
    className,
}: PanelSourceViewProps): ReactNode => {
    const { t } = useLingui();
    const rootClass = `psv-panel${className ? ` ${className}` : ""}`;

    if (model.type === "not-found") {
        return (
            <div className={rootClass}>
                <div className="psv-body psv-body--centered">
                    <EmptyState
                        icon={<Icon name="source" size="lg" />}
                        title={t`This source couldn't be found`}
                        body={t`It may have been removed, or isn't available right now.`}
                    />
                </div>
            </div>
        );
    }

    return (
        <div className={rootClass}>
            <header className="psv-header">
                <div className="psv-meta-row">
                    <Badge>
                        {model.meta.kind !== "" ? model.meta.kind : t`Source`}
                    </Badge>
                    {/* A small, NON-blocking affordance — the file below
                        renders exactly as normal while this shows. Never a
                        full-panel spinner that hides the file (owner
                        correction): analysis running is orthogonal to
                        content being readable. */}
                    {model.analysis.type === "pending" ? (
                        <Badge icon={<Spinner size="sm" />}>
                            {t`Analyzing…`}
                        </Badge>
                    ) : null}
                    {model.meta.filename !== undefined &&
                    model.meta.filename !== model.meta.title ? (
                        <Mono tone="faint">{model.meta.filename}</Mono>
                    ) : null}
                </div>
                <Heading
                    level={2}
                    size="lg"
                    className="psv-title bilda-heading--clamp"
                    title={model.meta.title}
                >
                    {model.meta.title}
                </Heading>
            </header>

            <div className="psv-body">
                {model.content.type === "text" ? (
                    <TextBody blocks={model.content.blocks} />
                ) : (
                    // Every OTHER readable kind — PDF, image, plain
                    // audio/video, synced video, and the honest `unavailable`
                    // floor — delegates to the SHARED per-kind registry, so
                    // the panel shows exactly what the full centre view does.
                    // A PDF gets the paginated reader (page control, opens at
                    // and marks `focusPages`: a chat reference's pages must be
                    // findable and verifiable here). No `sourceId`, so no
                    // annotation chrome leaks into the narrow pane.
                    <SourceReading
                        projection={model.content}
                        focusPages={focusPages}
                        onReupload={onReupload}
                    />
                )}
            </div>
        </div>
    );
};

// The text/markdown reading surface — sectioned paragraphs, no interaction.
// Empty extraction degrades to the honest empty floor rather than a blank
// canvas, same posture as SourceRenderer.tsx's `FlatReading`.
const TextBody = ({
    blocks,
}: {
    readonly blocks: readonly SourceBlock[];
}): ReactNode => {
    const { t } = useLingui();
    // The first quoted passage scrolls into view when the source opens. A DOM
    // read/write at the mount edge, not state; only the panel body scrolls
    // (scrollIntoView would also shift outer boxes).
    const firstMark = useRef<HTMLElement | null>(null);
    useEffect(() => {
        const mark = firstMark.current;
        const body = mark?.closest(".psv-body");
        if (mark === null || mark === undefined || !body) return;
        body.scrollTop +=
            mark.getBoundingClientRect().top -
            body.getBoundingClientRect().top -
            body.clientHeight / 3;
    }, []);
    const firstHighlightId = blocks.find((b) => b.type === "highlighted")?.id;
    if (blocks.length === 0) {
        return (
            <EmptyState
                title={t`Nothing to read yet`}
                body={t`No readable text was extracted from this source.`}
            />
        );
    }
    return (
        <article className="psv-reading">
            {blocks.map((block) => (
                <p key={block.id} className="psv-block">
                    {block.type === "highlighted" ? (
                        <mark
                            className="psv-mark"
                            ref={
                                block.id === firstHighlightId ? firstMark : null
                            }
                        >
                            {block.text}
                        </mark>
                    ) : (
                        block.text
                    )}
                </p>
            ))}
        </article>
    );
};
